import React, { useState, useCallback } from "react";
import { View, StyleSheet, Image, Pressable, Platform, ActivityIndicator } from "react-native";
import * as ImagePicker from "expo-image-picker";
import * as Device from "expo-device";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface PhotoFile {
  id: string;
  uri: string;
  name: string;
  type: string;
}

// Helper to infer mimetype from filename extension
function getMimeTypeFromFileName(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
    bmp: "image/bmp",
  };
  return mimeTypes[ext || ""] || "image/jpeg";
}

export interface PhotoCaptureProps {
  photos: PhotoFile[];
  onPhotosChange: (photos: PhotoFile[]) => void;
  maxPhotos?: number;
  placeholder?: string;
  disabled?: boolean;
}

export function PhotoCapture({
  photos,
  onPhotosChange,
  maxPhotos = 3,
  placeholder = "Add photos",
  disabled = false,
}: PhotoCaptureProps) {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(false);

  const canAddMore = photos.length < maxPhotos;

  const handleTakePhoto = useCallback(async () => {
    if (!canAddMore || disabled) return;

    try {
      setLoading(true);

      // On web, use HTML input with capture attribute to access camera
      if (Platform.OS === "web") {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.setAttribute("capture", "environment"); // back camera on mobile web
        input.style.display = "none";

        input.onchange = (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            const uri = URL.createObjectURL(file);
            const newPhoto: PhotoFile = {
              id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              uri,
              name: file.name || `photo_${Date.now()}.jpg`,
              type: file.type || "image/jpeg",
            };
            onPhotosChange([...photos, newPhoto]);
          }
          input.remove();
          setLoading(false);
        };

        input.oncancel = () => {
          input.remove();
          setLoading(false);
        };

        document.body.appendChild(input);
        input.click();
        return;
      }

      // Native: use expo-image-picker
      const permissionResult = await ImagePicker.requestCameraPermissionsAsync();
      if (!permissionResult.granted) {
        console.warn("Camera permission denied");
        setLoading(false);
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        const fileName = asset.fileName || `photo_${Date.now()}.jpg`;
        const newPhoto: PhotoFile = {
          id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          uri: asset.uri,
          name: fileName,
          type: asset.mimeType || getMimeTypeFromFileName(fileName),
        };
        onPhotosChange([...photos, newPhoto]);
      }
    } catch (error) {
      console.error("Error taking photo:", error);
    } finally {
      setLoading(false);
    }
  }, [canAddMore, disabled, photos, onPhotosChange]);

  const handlePickFromGallery = useCallback(async () => {
    if (!canAddMore || disabled) return;

    try {
      setLoading(true);

      // Request media library permissions
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permissionResult.granted) {
        console.warn("Media library permission denied");
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        selectionLimit: maxPhotos - photos.length,
        quality: 0.8,
      });

      if (!result.canceled && result.assets) {
        const newPhotos: PhotoFile[] = result.assets.map((asset) => {
          const fileName = asset.fileName || `image_${Date.now()}.jpg`;
          return {
            id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            uri: asset.uri,
            name: fileName,
            type: asset.mimeType || getMimeTypeFromFileName(fileName),
          };
        });
        onPhotosChange([...photos, ...newPhotos].slice(0, maxPhotos));
      }
    } catch (error) {
      console.error("Error picking images:", error);
    } finally {
      setLoading(false);
    }
  }, [canAddMore, disabled, maxPhotos, photos, onPhotosChange]);

  const handleRemovePhoto = useCallback(
    (photoId: string) => {
      onPhotosChange(photos.filter((p) => p.id !== photoId));
    },
    [photos, onPhotosChange],
  );

  return (
    <View style={styles.container}>
      {/* Photo Grid */}
      {photos.length > 0 && (
        <View style={styles.photoGrid}>
          {photos.map((photo) => (
            <View key={photo.id} style={[styles.photoContainer, { borderColor: colors.border }]}>
              <Image source={{ uri: photo.uri }} style={styles.photo} resizeMode="cover" />
              {!disabled && (
                <Pressable
                  onPress={() => handleRemovePhoto(photo.id)}
                  style={[styles.removeButton, { backgroundColor: colors.red.background }]}
                >
                  <MaterialCommunityIcons name="close" size={14} color="white" />
                </Pressable>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Add Photo Buttons */}
      {canAddMore && !disabled && (
        <View style={styles.buttonContainer}>
          {loading ? (
            <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
              <ActivityIndicator size="small" color={colors.primary} />
              <ThemedText style={[styles.loadingText, { color: colors.textSecondary }]}>Processing...</ThemedText>
            </View>
          ) : (
            <>
              {/* Show camera on native or mobile web */}
              {(Platform.OS !== "web" ||
                Device.deviceType === Device.DeviceType.PHONE ||
                Device.deviceType === Device.DeviceType.TABLET) && (
                <Button
                  text="Take Photo"
                  icon="camera"
                  onPress={handleTakePhoto}
                  style={[styles.button, { backgroundColor: colors.primary }]}
                  textStyle={{ color: "white" }}
                />
              )}
              <Button
                text="Choose from Gallery"
                icon="image"
                onPress={handlePickFromGallery}
                style={[styles.button, { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.border }]}
              />
            </>
          )}
        </View>
      )}

      {/* Empty State */}
      {photos.length === 0 && !loading && (
        <View style={[styles.emptyState, { backgroundColor: colors.background, borderColor: colors.border }]}>
          <MaterialCommunityIcons name="camera-outline" size={32} color={colors.placeholder} />
          <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>{placeholder}</ThemedText>
          <ThemedText style={[styles.limitText, { color: colors.placeholder }]}>
            Up to {maxPhotos} photo{maxPhotos > 1 ? "s" : ""}
          </ThemedText>
        </View>
      )}

      {/* Photo Count */}
      {photos.length > 0 && (
        <ThemedText style={[styles.countText, { color: colors.textSecondary }]}>
          {photos.length} of {maxPhotos} photo{maxPhotos > 1 ? "s" : ""}
        </ThemedText>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  photoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  photoContainer: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 1,
    overflow: "hidden",
    position: "relative",
  },
  photo: {
    width: "100%",
    height: "100%",
  },
  removeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 8,
  },
  button: {
    flex: 1,
    height: 44,
  },
  loadingContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
  },
  emptyState: {
    padding: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "dashed",
    alignItems: "center",
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: "500",
  },
  limitText: {
    fontSize: 12,
  },
  countText: {
    fontSize: 12,
    textAlign: "center",
  },
});

export default PhotoCapture;

/**
 * Utility to convert PhotoFile to File object for upload
 */
export async function photoFileToFile(photo: PhotoFile): Promise<File> {
  const response = await fetch(photo.uri);
  const blob = await response.blob();
  return new File([blob], photo.name, { type: photo.type });
}

/**
 * Utility to convert PhotoFile to base64-encoded data for edge function upload
 */
export async function photoFileToBase64(photo: PhotoFile): Promise<{ base64: string; name: string; type: string }> {
  const response = await fetch(photo.uri);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1]; // strip data:mime;base64, prefix
      resolve({ base64, name: photo.name, type: photo.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
